import React from 'react'
import { useNavigate } from 'react-router-dom'
import './Register.css'
import { useStructuraAuth } from './useStructuraAuth'
import { useTranslation, TranslationKey } from '@structura/i18n'

interface BenefitCard {
  id: number
  icon: string
  titleKey: TranslationKey
  descKey: TranslationKey
  accent: string
}

const benefitCards: BenefitCard[] = [
  {
    id: 1,
    icon: 'bi-lightning-charge-fill',
    titleKey: 'auth.register.benefit1Title',
    descKey: 'auth.register.benefit1Desc',
    accent: 'seagreen'
  },
  {
    id: 2,
    icon: 'bi-bar-chart-fill',
    titleKey: 'auth.register.benefit2Title',
    descKey: 'auth.register.benefit2Desc',
    accent: 'goldenrod'
  },
  {
    id: 3,
    icon: 'bi-gem',
    titleKey: 'auth.register.benefit3Title',
    descKey: 'auth.register.benefit3Desc',
    accent: 'sienna'
  }
]

export interface RegisterProps {
  // Per-app beam designer route (e.g. "/beam/wood"). After successful
  // registration the user is sent here so they land in their app's designer,
  // not a different app's. Required because the post-register destination
  // is meaningless without app context.
  beamRoute: string
}

const Register: React.FC<RegisterProps> = ({ beamRoute }) => {
  const navigate = useNavigate()
  const authState = useStructuraAuth()
  const { t } = useTranslation()
  const [email, setEmail] = React.useState<string>('')
  const [name, setName] = React.useState<string>('')
  const [companyName, setCompanyName] = React.useState<string>('')
  const [password, setPassword] = React.useState<string>('')
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null)

  const handleRegisterClick = React.useCallback(async (): Promise<void> => {
    setErrorMessage(null)
    setSuccessMessage(null)

    if (authState.isAuthenticated) {
      navigate('/account')
      return
    }

    try {
      const outcome = await authState.register(email, password, name.length > 0 ? name : undefined, companyName.length > 0 ? companyName : undefined)
      if (outcome === 'verification-pending') {
        setSuccessMessage(`${t('auth.register.pendingTitle')} — ${t('auth.register.pendingBody')}`)
        return
      }
      setSuccessMessage(t('auth.register.success'))
      navigate(beamRoute)
    } catch (error) {
      const message: string = error instanceof Error ? error.message : t('auth.register.errGeneric')
      setErrorMessage(message)
    }
  }, [authState, email, password, name, companyName, navigate, beamRoute, t])

  const handleAccountClick = React.useCallback((): void => {
    navigate('/account')
  }, [navigate])

  const heroSubtitle: string = authState.isAuthenticated
    ? t('auth.register.heroAuthenticated')
    : t('auth.register.heroAnonymous')

  return (
    <div className="register-page">
      <div className="register-hero">
        <div className="container">
          <div className="row align-items-center gy-4 justify-content-center text-center">
            <div className="col-xl-8 col-lg-9">
              <div className="register-badge mx-auto">
                <span className="badge-text">{t('auth.register.badgeEngineers')}</span>
                <span className="badge-heart" aria-hidden="true">&#9829;</span>
                <span className="badge-text">StructuraMatrix</span>
                <span className="visually-hidden">{t('auth.register.badgeSr')}</span>
              </div>
              <h1 className="h1 fw-bold mb-3 text-dark">{t('auth.register.startTrial')}</h1>
              {heroSubtitle.length > 0 && (
                <p className="lead text-muted mb-2">
                  {heroSubtitle}
                </p>
              )}
              <p className="text-muted mb-4">
                <i className="bi bi-check-circle-fill text-success me-1"></i>
                {t('auth.register.noCreditCard')}
              </p>
              <div className="d-flex flex-wrap gap-3 justify-content-center">
                <button
                  type="button"
                  className="btn btn-register-primary btn-lg mb-3"
                  onClick={handleRegisterClick}
                  disabled={authState.isLoading || email.length === 0 || password.length === 0}
                >
                  <i className="bi bi-arrow-right-circle me-2"></i>
                  {authState.isAuthenticated ? t('auth.register.alreadyRegistered') : t('auth.register.registerNow')}
                </button>
                {!authState.isAuthenticated && (
                  <form
                    className="mt-3 text-start"
                    onSubmit={(event) => {
                      event.preventDefault()
                      void handleRegisterClick()
                    }}
                  >
                    <div className="mb-3">
                      <label htmlFor="registerEmail" className="form-label">
                        {t('auth.register.email')}
                      </label>
                      <input
                        id="registerEmail"
                        type="email"
                        className="form-control"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="registerName" className="form-label">
                        {t('auth.register.nameOptional')}
                      </label>
                      <input
                        id="registerName"
                        type="text"
                        className="form-control"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="registerCompanyName" className="form-label">
                        {t('auth.register.companyOptional')}
                      </label>
                      <input
                        id="registerCompanyName"
                        type="text"
                        className="form-control"
                        value={companyName}
                        onChange={(event) => setCompanyName(event.target.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="registerPassword" className="form-label">
                        {t('auth.register.password')}
                      </label>
                      <input
                        id="registerPassword"
                        type="password"
                        className="form-control"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        required
                      />
                    </div>
                  </form>
                )}
                {authState.isAuthenticated && (
                  <button
                    type="button"
                    className="btn btn-outline-dark btn-lg mb-3"
                    onClick={handleAccountClick}
                  >
                    <i className="bi bi-person-badge me-2"></i>
                    {t('auth.register.viewAccount')}
                  </button>
                )}
              </div>
              {successMessage !== null && (
                <div className="alert alert-success mt-4 mb-0" role="alert">
                  {successMessage}
                </div>
              )}
              {errorMessage !== null && (
                <div className="alert alert-warning mt-4 mb-0" role="alert">
                  {errorMessage}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <section className="register-benefits-section py-5">
        <div className="container">
          <h2 className="section-title text-center mb-5">{t('auth.register.whyTrial')}</h2>
          <div className="row g-4 justify-content-center">
            {benefitCards.map((card: BenefitCard) => (
              <div key={card.id} className="col-md-6 col-lg-4">
                <article
                  className="register-benefit-card h-100"
                  style={{ '--accent-color': card.accent } as React.CSSProperties}
                >
                  <div className="benefit-icon">
                    <i className={`bi ${card.icon}`}></i>
                  </div>
                  <h3 className="benefit-title">{t(card.titleKey)}</h3>
                  <p className="benefit-copy">{t(card.descKey)}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-5" style={{ background: 'linear-gradient(180deg, white 0%, rgba(74, 124, 89, 0.05) 100%)' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title mb-3">{t('auth.register.pricingTitle')}</h2>
            <p className="text-muted">{t('auth.register.pricingSubtitle')}</p>
          </div>
          
          <div className="row justify-content-center">
            <div className="col-lg-5 col-md-8">
              <div 
                className="card border-0 shadow-lg position-relative overflow-hidden"
                style={{ borderRadius: '1.25rem' }}
              >
                {/* Popular badge */}
                <div 
                  className="position-absolute top-0 end-0 px-3 py-2"
                  style={{ 
                    background: 'linear-gradient(135deg, forestgreen, seagreen)',
                    borderRadius: '0 1.25rem 0 1rem',
                    color: 'white',
                    fontSize: '0.8rem',
                    fontWeight: 600
                  }}
                >
                  <i className="bi bi-star-fill me-1"></i>
                  {t('auth.register.professional')}
                </div>

                <div className="card-body p-4 p-lg-5">
                  {/* Trial highlight */}
                  <div 
                    className="d-inline-block px-3 py-1 rounded-pill mb-4"
                    style={{ background: 'rgba(34, 139, 34, 0.1)', color: 'forestgreen', fontWeight: 600 }}
                  >
                    <i className="bi bi-gift me-1"></i>
                    {t('auth.register.thirtyDayTrial')}
                  </div>

                  {/* Price */}
                  <div className="mb-4">
                    <div className="d-flex align-items-baseline justify-content-center gap-2 flex-wrap">
                      <div className="d-flex align-items-baseline">
                        <span className="h2 fw-bold mb-0" style={{ color: '#2d2d2d' }}>€110</span>
                        <span className="text-muted ms-1">{t('auth.register.perMonth')}</span>
                      </div>
                      <span className="text-muted">{t('auth.register.or')}</span>
                      <div className="d-flex align-items-baseline">
                        <span className="h2 fw-bold mb-0" style={{ color: '#2d2d2d' }}>1 100 NOK</span>
                        <span className="text-muted ms-1">{t('auth.register.perMonth')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Features */}
                  <ul className="list-unstyled mb-4">
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-check2-circle text-success me-2 mt-1"></i>
                      <span>{t('auth.register.feat1')}</span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-check2-circle text-success me-2 mt-1"></i>
                      <span>{t('auth.register.feat2')}</span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-check2-circle text-success me-2 mt-1"></i>
                      <span>{t('auth.register.feat3')}</span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-check2-circle text-success me-2 mt-1"></i>
                      <span>{t('auth.register.feat4')}</span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-check2-circle text-success me-2 mt-1"></i>
                      <span>{t('auth.register.feat5')}</span>
                    </li>
                    <li className="d-flex align-items-start">
                      <i className="bi bi-check2-circle text-success me-2 mt-1"></i>
                      <span>{t('auth.register.feat6')}</span>
                    </li>
                  </ul>

                  {/* CTA */}
                  <div className="text-center">
                    <p className="small text-muted mb-0">
                      <i className="bi bi-shield-check me-1"></i>
                      {t('auth.register.cancelAnytime')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ note */}
          <div className="text-center mt-5">
            <p className="text-muted mb-0">
              {t('auth.register.pricingQuestions')}{' '}
              <a href="mailto:support@structuramatrix.com" className="text-decoration-none" style={{ color: 'forestgreen' }}>
                {t('auth.register.contactUs')}
              </a>
            </p>
          </div>
        </div>
      </section>

    </div>
  )
}

export default Register


